import { useEffect, useRef, useState } from "react";
import { SparklesIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { classNames } from "@/components/utils/functions";

interface ChatMessage {
  id: string;
  type: "user" | "assistant";
  content: string;
  timestamp: Date;
  action?: "generate_outfit" | "update_tags" | "update_colors";
}

interface ChatInterfaceProps {
  messages: ChatMessage[];
  inputMessage: string;
  setInputMessage: (message: string) => void;
  isTyping: boolean;
  generatingOutfit: boolean;
  onSendMessage: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
}

export default function ChatInterface({
  messages,
  inputMessage,
  setInputMessage,
  isTyping,
  generatingOutfit,
  onSendMessage,
  onKeyDown,
}: ChatInterfaceProps) {
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom of messages within container only
  useEffect(() => {
    if (messagesContainerRef.current) {
      // Use requestAnimationFrame to ensure scroll happens after paint
      requestAnimationFrame(() => {
        if (messagesContainerRef.current) {
          const container = messagesContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }
      });
    }
  }, [messages]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 overflow-hidden flex flex-col h-full">
      {/* Chat Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-pink-500 to-purple-500 flex items-center justify-center">
            <SparklesIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-light tracking-wider uppercase text-gray-900 dark:text-white">
              STYLIST ASSISTANT
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Your personal fashion advisor
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-6 space-y-4"
        style={{ overscrollBehavior: "contain" }}
      >
        {messages.map((message) => (
          <div
            key={message.id}
            className={classNames(
              "flex",
              message.type === "user" ? "justify-end" : "justify-start"
            )}
          >
            <div
              className={classNames(
                "max-w-xs lg:max-w-md px-4 py-2 rounded-2xl",
                message.type === "user"
                  ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
              )}
            >
              <p className="text-sm font-light leading-relaxed">
                {message.content}
              </p>
            </div>
          </div>
        ))}

        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-2xl">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></div>
                <div
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="p-6 border-t border-gray-200 dark:border-gray-700">
        {generatingOutfit && (
          <div className="mb-3 flex items-center text-xs text-gray-500 dark:text-gray-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
            Generating outfit image…
          </div>
        )}
        <div className="flex space-x-3">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about styling, colors, or creating outfits..."
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white focus:border-transparent"
            disabled={isTyping || generatingOutfit}
          />
          <button
            onClick={onSendMessage}
            disabled={!inputMessage.trim() || isTyping || generatingOutfit}
            className="p-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl hover:scale-105 transition-transform duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
